import type { NodeId } from "~/models/dto";
import { getTime } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { Tuple } from "~/tools/tuple";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import { type BoundMetricId, getComponentBoundMetricFn, getMetricPerspectiveDataFn } from "../../common/openapi";
import type { UseFullQueryOptions } from "../react-query";

export const getKeyForBoundMetricId = (bmi: BoundMetricId | undefined) => {
	if (!bmi) {
		return undefined;
	}
	switch (bmi._type) {
		case "BoundMetricBindingId":
			return bmi.identifier;
		case "BoundPresentationMetricId":
			return `${bmi.presentationIdentifier ?? ""}-${bmi.metricId}`;
	}
};

export interface QueryComponentBoundMetricParams {
	componentIdOrIdentifier: string | NodeId;
	boundMetricId: BoundMetricId;
	timestamp: undefined | Time;
}

export const queryComponentBoundMetric = (({ componentIdOrIdentifier, boundMetricId, timestamp }) => {
	function queryFn() {
		return getComponentBoundMetricFn({
			componentIdOrIdentifier: componentIdOrIdentifier.toString(),
			topologyTime: getTime(timestamp),
			body: boundMetricId,
		});
	}

	return {
		queryKey: Tuple("components", componentIdOrIdentifier, "binding", boundMetricId),
		queryFn,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		timestamp,
	} as const;
}) satisfies (params: QueryComponentBoundMetricParams) => UseFullQueryOptions;

export interface QueryNonEmptyComponentBoundMetricParams {
	componentIdOrIdentifier: NodeId | string;
	timestamp: undefined | Time;
	timeRange: TimeRange;
}

export const queryMetricPerspectiveData = (({ componentIdOrIdentifier, timestamp, timeRange }) => {
	function queryFn() {
		const interval = timeRange.toInterval();

		return getMetricPerspectiveDataFn({
			componentIdOrIdentifier: componentIdOrIdentifier.toString(),
			topologyTime: getTime(timestamp),
			startTime: getTime(interval.start),
			endTime: getTime(interval.end),
		});
	}

	return {
		queryKey: Tuple("components", componentIdOrIdentifier, "binding"),
		queryFn,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		timestamp,
		timeRange,
	} as const;
}) satisfies (params: QueryNonEmptyComponentBoundMetricParams) => UseFullQueryOptions;
